import type { TFunction } from 'i18next';
import type { ReservedStaffTimeResponse } from '../../api/generated';
import type { TableColumn } from '../UI';
import { actionsColumn, entityNameCell, EMPTY, reasonColumn } from '../Bookings/shared';
import sharedStyles from '../Bookings/shared/bookings.module.css';
import {
  formatEligibleDays,
  formatPlacementPeriod,
  formatTimeOfDay
} from './reservedStaffTimeBookingFormat';

interface ReservedStaffTimeColumnHandlers {
  staffNameById: Map<string, string>;
  onEdit: (booking: ReservedStaffTimeResponse) => void;
  onDelete: (booking: ReservedStaffTimeResponse) => void;
}

const DURATION_COL_WIDTH = 120;
const PERIOD_COL_WIDTH = 200;
const REPEATS_COL_WIDTH = 180;
const TIME_COL_WIDTH = 140;

export const getReservedStaffTimeBookingColumns = (
  t: TFunction,
  { staffNameById, onEdit, onDelete }: ReservedStaffTimeColumnHandlers
): TableColumn<ReservedStaffTimeResponse>[] => [
  {
    key: 'staff',
    header: t('reserved-staff-time-col-staff'),
    render: (booking) => {
      const name = staffNameById.get(booking.staffId);
      return entityNameCell({ names: name ? [name] : [] });
    }
  },
  {
    key: 'duration',
    header: t('reserved-staff-time-col-duration'),
    width: DURATION_COL_WIDTH,
    render: (booking) => (
      <span>{t('reserved-staff-time-duration-value', { minutes: booking.durationMinutes })}</span>
    )
  },
  {
    key: 'period',
    header: t('reserved-staff-time-col-period'),
    width: PERIOD_COL_WIDTH,
    render: (booking) => <span>{formatPlacementPeriod(booking.placement)}</span>
  },
  {
    key: 'repeats',
    header: t('reserved-staff-time-col-repeats'),
    width: REPEATS_COL_WIDTH,
    render: (booking) =>
      booking.placement.recurrence ? (
        <span>{formatEligibleDays(t, booking.placement.recurrence)}</span>
      ) : (
        <span className={sharedStyles.muted}>{EMPTY}</span>
      )
  },
  {
    key: 'time',
    header: t('reserved-staff-time-col-time'),
    width: TIME_COL_WIDTH,
    render: (booking) => <span>{formatTimeOfDay(t, booking.placement)}</span>
  },
  reasonColumn(t, 'reserved-staff-time-col-reason'),
  actionsColumn(t, {
    editKey: 'reserved-staff-time-edit',
    deleteKey: 'reserved-staff-time-delete-title',
    onEdit,
    onDelete
  })
];
